import { Moment } from 'moment-timezone';
import type { Period } from '@spike/model';
import type { RoomsOperatingReport, RoomsOperatingMetric, RoomsOperatingPeriods } from 'model/RoomsOperatingReport';

type MetricKind = 'currency' | 'count' | 'percent' | 'nights';

const SECTION_ORDER: RoomsOperatingMetric['section'][] = ['Revenue', 'Pets', 'Rates', 'Rooms', 'Sales'];

const fmtCurrency = (n: number) =>
    n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const fmtNumber = (n: number) => n.toLocaleString();
const fmtPercent = (n: number) => `${(n * 100).toFixed(0)}%`;

const deltaPct = (current: number, previous: number) => {
    // EXACTAMENTE como la tabla:
    if (previous === 0) return 1;
    return (current - previous) / previous;
};

const valueFor = (m: RoomsOperatingMetric, scope: 'today' | 'mtd' | 'ytd') => {
    const { value } = m[scope];
    switch (m.kind as MetricKind) {
        case 'currency':
            return fmtCurrency(value);
        case 'percent':
            return fmtPercent(value);
        case 'nights':
            return value.toFixed(1);
        default:
            return fmtNumber(value);
    }
};

const lyValueFor = (m: RoomsOperatingMetric, scope: 'today' | 'mtd' | 'ytd') => {
    const { ly } = m[scope];
    switch (m.kind as MetricKind) {
        case 'currency':
            return fmtCurrency(ly);
        case 'percent':
            return fmtPercent(ly);
        case 'nights':
            return ly.toFixed(1);
        default:
            return fmtNumber(ly);
    }
};

const deltaTextFor = (m: RoomsOperatingMetric, scope: 'today' | 'mtd' | 'ytd') => {
    const { value, ly } = m[scope];
    const pct = deltaPct(value, ly);
    const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
    return `${sign}${Math.abs(pct * 100).toFixed(0)}%`;
};

// Labels (idéntico al Table)
const formatSingleLabel = (d: Moment, tz: string) => d.clone().tz(tz).format('MMM D, YYYY');
const formatRangeLabel = (p: Period, tz: string) =>
    `${p.from.clone().tz(tz).format('MM/DD/YY')} - ${p.to.clone().tz(tz).format('MM/DD/YY')}`;

const buildTodayLabel = (periods: RoomsOperatingPeriods, tz: string) => formatSingleLabel(periods.period.from, tz);

const buildMtdLabel = (periods: RoomsOperatingPeriods, tz: string) => {
    const end = periods.period.to.clone().tz(tz);
    const from = end.clone().startOf('month').startOf('day');
    const to = end.clone().endOf('day');
    return `${from.format('MM/DD/YY')} - ${to.format('MM/DD/YY')}`;
};

const buildYtdLabel = (periods: RoomsOperatingPeriods, tz: string) => {
    const end = periods.period.to.clone().tz(tz);
    const from = end.clone().startOf('year').startOf('day');
    const to = end.clone().endOf('day');
    return `${from.format('MM/DD/YY')} - ${to.format('MM/DD/YY')}`;
};

const toCSVCell = (v: unknown) => {
    const s = String(v ?? '');
    const escaped = s.replace(/"/g, '""');
    return /[,\n"]/.test(escaped) ? `"${escaped}"` : escaped;
};

export const buildRoomsOperatingCSV = (
    report: RoomsOperatingReport,
    periods: RoomsOperatingPeriods,
    timeZone: string
) => {
    const metrics = report?.metrics ?? [];

    const todayLabel = buildTodayLabel(periods, timeZone);
    const mtdLabel = buildMtdLabel(periods, timeZone);
    const ytdLabel = buildYtdLabel(periods, timeZone);

    const todayLyLabel = formatSingleLabel(periods.compareToday, timeZone);
    const mtdLyLabel = formatRangeLabel(periods.compareMtd, timeZone);
    const ytdLyLabel = formatRangeLabel(periods.compareYtd, timeZone);

    const header = [
        '',
        todayLabel,
        `Compare to (${todayLyLabel})`,
        '%',
        mtdLabel,
        `Compare to (${mtdLyLabel})`,
        '%',
        ytdLabel,
        `Compare to (${ytdLyLabel})`,
        '%'
    ];

    const rows: unknown[][] = [header];

    SECTION_ORDER.forEach(section => {
        const group = metrics.filter(m => m.section === section);
        if (!group.length) return;

        group.forEach(m => {
            rows.push([
                m.label,
                valueFor(m, 'today'),
                lyValueFor(m, 'today'),
                deltaTextFor(m, 'today'),

                valueFor(m, 'mtd'),
                lyValueFor(m, 'mtd'),
                deltaTextFor(m, 'mtd'),

                valueFor(m, 'ytd'),
                lyValueFor(m, 'ytd'),
                deltaTextFor(m, 'ytd')
            ]);
        });
    });

    // BOM para Excel
    return '\uFEFF' + rows.map(r => r.map(toCSVCell).join(',')).join('\n');
};

export const downloadCSV = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
};

export const defaultRoomsOperatingFilename = (periods: RoomsOperatingPeriods, tz: string) => {
    // opcional: nombre similar a lo que ves
    const day = periods.period.from.clone().tz(tz).format('YYYYMMDD');
    return `rooms_operating_${day}.csv`;
};
